import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { NgxMaskModule } from 'ngx-mask';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { PhoneE164Validators } from '@app/core/validators/phone-e164.validator';
import {
  IPhoneCodeModel,
  PhoneCodeModelList,
} from '@app/shared/models/phone-code.model';

@Component({
  selector: 'app-bt-phone-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    NgxMaskModule,
  ],
  templateUrl: './bt-phone-input.component.html',
  styleUrls: ['./bt-phone-input.component.scss'],
})
export class BtPhoneInputComponent implements OnInit, OnDestroy {
  @Input() parentFormControl = new FormControl();
  @Input() public loginDisabled: boolean = false;
  @Input() public readonly: boolean = false;

  @Output() public invalid = new EventEmitter<boolean>();

  phoneControl = new FormControl('', Validators.required);
  phoneValidationControl = new FormControl('', [
    Validators.required,
    PhoneE164Validators,
  ]);
  dealCodeControl = new FormControl<string | IPhoneCodeModel>(
    PhoneCodeModelList.find(item => item.iso === 'US'),
    Validators.required,
  );
  filteredOptions$: Observable<Array<IPhoneCodeModel>>;
  phone: string;

  private destroy$: Subject<void> = new Subject();

  constructor() {}

  ngOnInit(): void {
    this.phoneControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.loginDisabled = true;
        if (typeof this.dealCodeControl.value !== 'string') {
          this.phone = this.dealCodeControl.value.code + (value as string);
          this.phoneValidationControl.patchValue(`+${this.phone}`);
          this.loginDisabled =
            this.phone.length < 11 && value.replace('-', '').length < 8;
          this.setPhoneValue();
          return;
        }
        this.dealCodeControl.setValue(
          this._filter(this.dealCodeControl.value)[0],
        );
        this.setPhoneValue();
      });
    this.dealCodeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.loginDisabled = true;
        if (
          this.dealCodeControl?.value &&
          typeof this.dealCodeControl.value !== 'string'
        ) {
          this.phone =
            this.dealCodeControl.value.code +
            (this.phoneControl.value as string);
          this.phoneValidationControl.patchValue(`+${this.phone}`);
          this.loginDisabled =
            this.phone.length < 11 &&
            this.phoneControl.value.replace('-', '').length < 8;
          this.setPhoneValue();
          return;
        }
        this.phone = value + (this.phoneControl.value as string);
        this.phoneValidationControl.patchValue(`+${this.phone}`);
        this.loginDisabled =
          this.phone.length < 11 &&
          this.phoneControl.value.replace('-', '').length < 8;
        this.setPhoneValue();
      });

    this.filteredOptions$ = this.dealCodeControl.valueChanges.pipe(
      startWith(''),
      map(value =>
        this._filter(typeof value === 'string' ? value || '' : value?.country),
      ),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayFn(phoneCode: IPhoneCodeModel): string {
    return phoneCode && phoneCode.code ? phoneCode.code : '';
  }

  markParentAsTouched(): void {
    this.parentFormControl.markAsTouched();
    this.parentFormControl.markAsDirty();
  }

  private _filter(value: string): Array<IPhoneCodeModel> {
    const filterValue = value.toLowerCase();

    return PhoneCodeModelList.filter(
      option =>
        option.country.toLowerCase().includes(filterValue) ||
        option.code.toLowerCase().includes(filterValue) ||
        option.iso.toLowerCase().includes(filterValue),
    );
  }

  private setPhoneValue(): void {
    this.invalid.emit(
      this.phoneValidationControl.hasError('phoneE164') ||
        this.dealCodeControl.invalid,
    );
    if (this.phoneControl.dirty) {
      this.parentFormControl.setValue(`+${this.phone}`);
      if (this.phoneValidationControl.hasError('phoneE164')) {
        this.parentFormControl.setErrors(this.phoneValidationControl.errors);
      }
    }
  }
}
